/**
 * The Backup board's drill-down URL vocabulary (dashboard design rules §4.2):
 * one place for the routes and search params the L1 sections and the L2
 * sub-routes share, so a heat cell, a "View all →" link and the L2 FilterRow
 * can never disagree about what `?result=` means.
 */

/** L1 board. */
export const BACKUP_PATH = "/backup";

/** L2 sub-route: every job, filterable. */
export const BACKUP_JOBS_PATH = "/backup/jobs";

/** L2 sub-route: every server against every protection source. */
export const BACKUP_COVERAGE_PATH = "/backup/coverage";

/** L2 sub-route: the full task list, filterable by when it has to be done. */
export const BACKUP_ACTIONS_PATH = "/backup/actions";

/**
 * Multi-value param carrying action urgency (`now`, `soon`, `backlog`) — the
 * backend's `BackupAction.urgency`. Urgency is *when it has to be dealt with*,
 * which is the sort an operator does each morning, and deliberately not a
 * severity: a full repository and a failed backup are both "today".
 */
export const URGENCY_PARAM = "urgency";

/**
 * Multi-value param carrying the protection source (`veeam`, `azure-backup`).
 * Scopes the L1 sections; the L0 tiles stay estate-wide across sources, because
 * "backed up by something" is the question (concept §4).
 */
export const SOURCE_PARAM = "source";

/** Multi-value param carrying site codes (`HC`, `CNSHA`, …). */
export const SITE_PARAM = "site";

/**
 * Multi-value param carrying job outcomes, in the backend's **normalised**
 * vocabulary (`success`, `warning`, `failed`, `missed`, `running`,
 * `never-run`, `disabled`, `unknown`) — never the raw Veeam words, which say
 * `None` for two different things.
 */
export const RESULT_PARAM = "result";

/** Multi-value param carrying SLA tiers (`SLA01`…`SLA20`). */
export const SLA_PARAM = "sla";

/**
 * Multi-value param carrying coverage buckets (`none`, `excluded`, `orphaned`,
 * `both`, `veeam`, `azure-backup`).
 *
 * Five of the six are the backend's `BackupCoverageRow.state`; `excluded` is
 * the front end splitting `none` by whether the row carries an
 * `exclusion_kind`. The split is a filter concern rather than a state, because
 * the server's protection really is *none* either way — what differs is
 * whether anybody decided that on purpose.
 */
export const PROTECTION_PARAM = "protection";

/**
 * Single-value param carrying the job whose L2 drawer is open, encoded
 * `<source>:<name>` — job names never contain a colon.
 */
export const JOB_DRAWER_PARAM = "job";

/** Encode a job into the `?job=` drawer key. */
export function jobKey(source: string, name: string): string {
  return `${source}:${name}`;
}

/** Decode a `?job=` drawer key, or null when it is missing or malformed. */
export function parseJobKey(value: string | null): { source: string; name: string } | null {
  if (!value) {
    return null;
  }
  const separator = value.indexOf(":");
  if (separator <= 0 || separator === value.length - 1) {
    return null;
  }
  return { source: value.slice(0, separator), name: value.slice(separator + 1) };
}
